import axios from "axios";
import React, { useEffect, useState, useRef } from "react";
import {
  TRAINING_REPORTS_LIST,
  USER_LIST_DROPDOWN,
  COUNTRY_LIST_DROPDOWN,
  STATE_LIST_DROPDOWN,
  CITY_LIST_DROPDOWN,
  CHAPTER_LIST_DROPDOWN,
  REGION_LIST_DROPDOWN,
} from "../../routes/routes";
import { PAGINATE, NOPAGINATE } from "../../constant/constant";
import { NoDataFound, Pagination, hasRole } from "../../components/Utils";
import { usePermission } from "../../hooks/usePermission";
import { useForumReportDateRange } from "../../hooks/useForumReportDateRange";
import Select from "react-select";
import { toast } from "react-toastify";
import moment from "moment";
import Excel from "exceljs";
import { saveAs } from "file-saver";
import {
  styleExcelHeaderRow,
  styleExcelTitleRow,
} from "../../utils/excelStyles";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_red.css";
import Loader from "../../components/common/Loader";
import { Link } from "react-router-dom";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import StickyTable from "../../components/common/StickyTable";

const TrainingReportsList = () => {
  const { hasPermission } = usePermission();
  const canExport =
    hasPermission("reports-training-list-export") ||
    hasPermission("reports-attendance-list-export");

  // State Declarations
  const [data, setData] = useState([]);
  const [paginator, setPaginator] = useState({});
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [filter, setFilter] = useState({});
  const {
    from_date: forumFromDate,
    to_date: forumToDate,
    ready: forumRangeReady,
  } = useForumReportDateRange(filter?.chapter_id);
  const [userOptions, setUserOptions] = useState([]);
  const [btnExcelDisabled, setBtnExcelDisabled] = useState(false);
  const [btnPdfDisabled, setBtnPdfDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [chapterOptions, setChapterOptions] = useState([]);
  const [showClearBtn, setShowClearBtn] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
  const refFlatpickr = useRef(null);

  const selectStateRef = useRef(null);
  const selectCityRef = useRef(null);
  const selectRegionRef = useRef(null);
  const selectChapterRef = useRef(null);

  const handleStateClear = () => {
    selectStateRef.current?.clearValue();
    selectCityRef.current?.clearValue();
    selectRegionRef.current?.clearValue();
    selectChapterRef.current?.clearValue();
  };

  const handleCityClear = () => {
    selectCityRef.current?.clearValue();
    selectRegionRef.current?.clearValue();
    selectChapterRef.current?.clearValue();
  };

  const handleRegionClear = () => {
    selectRegionRef.current?.clearValue();
    selectChapterRef.current?.clearValue();
  };

  const handleChapterClear = () => {
    if (selectChapterRef.current) selectChapterRef.current.clearValue();
    if (refFlatpickr.current && refFlatpickr.current.flatpickr) {
      refFlatpickr.current.flatpickr.clear();
      setShowClearBtn(false);
    }
  };

  // Excel columns definition
  const columns = [{ header: "Sl.No.", key: "sno" }];
  columns.push({ header: "Member", key: "name" });
  columns.push({ header: "Member ID", key: "member_id" });
  columns.push({ header: "Company", key: "company_name" });
  columns.push({ header: "Mobile", key: "mobile_number" });
  columns.push({ header: "Total Trainings", key: "total_assigned" });
  columns.push({ header: "Attended", key: "attended_count" });
  columns.push({ header: "Absent", key: "absent_count" });
  columns.push({ header: "Not Marked", key: "unmarked_count" });
  columns.push({ header: "Attendance %", key: "attendance_percentage" });
  columns.push({ header: "Training Points", key: "training_points" });
  columns.push({ header: "Last Attended Date", key: "last_attended_date" });
  if (hasRole([1])) {
    columns.push({ header: "Country", key: "country_name" });
  }
  if (hasRole([1, 6])) {
    columns.push({ header: "State", key: "state_name" });
  }
  if (hasRole([1, 6, 7])) {
    columns.push({ header: "City", key: "city_name" });
  }
  if (hasRole([1, 6, 7, 8])) {
    columns.push({ header: "Region", key: "region_name" });
  }
  if (hasRole([1, 6, 7, 8, 2])) {
    columns.push({ header: "Circle", key: "chapter_name" });
  }

  // Excel title
  const { from_date, to_date } = filter;
  let workSheetName = `Training Reports`;
  if (from_date && to_date) {
    if (from_date === to_date) {
      workSheetName += ` (${moment(from_date, ["DD/MM/YYYY", "YYYY-MM-DD"], true).format("DD/MM/YYYY")})`;
    } else {
      workSheetName += ` (${moment(from_date, ["DD/MM/YYYY", "YYYY-MM-DD"], true).format("DD/MM/YYYY")} to ${moment(to_date, ["DD/MM/YYYY", "YYYY-MM-DD"], true).format("DD/MM/YYYY")})`;
    }
  } else {
    workSheetName += ` (Overall)`;
  }

  const title = workSheetName;
  const fileName = "Training Reports";

  const exportAsExcel = async () => {
    try {
      const workbook = new Excel.Workbook();
      const worksheet = workbook.addWorksheet(fileName);
      worksheet.columns = columns;

      worksheet.insertRow(1, []);

      const totalCols = columns.length || 1;
      const lastColLetter = String.fromCharCode(64 + totalCols);
      worksheet.mergeCells(`A1:${lastColLetter}1`);
      const titleCell = worksheet.getCell("A1");
      titleCell.value = title || "Training Reports";
      styleExcelTitleRow(worksheet.getRow(1), totalCols);
      styleExcelHeaderRow(worksheet.getRow(2));

      setBtnExcelDisabled(true);
      await axios
        .get(TRAINING_REPORTS_LIST, {
          params: {
            type: NOPAGINATE,
            isAdmin: 1,
            ...filter,
          },
        })
        .then(async (response) => {
          if (response.status === 200 && response.data.success) {
            const list = response.data.data.users || [];
            if (list.length < 1) {
              toast.error("No data found!");
              return;
            }
            list.forEach((row, index) => {
              worksheet.addRow({
                sno: index + 1,
                name: row.name,
                member_id: row.member_id || "-",
                company_name: row.company_name || "-",
                mobile_number: row.mobile_number ? `+${row.mobile_country_code || 91} ${row.mobile_number}` : "-",
                total_assigned: row.total_assigned,
                attended_count: row.attended_count,
                absent_count: row.absent_count,
                unmarked_count: row.unmarked_count,
                attendance_percentage: `${row.attendance_percentage}%`,
                training_points: row.training_points,
                last_attended_date: row.last_attended_date || "-",
                country_name: row.country_name,
                state_name: row.state_name,
                city_name: row.city_name,
                region_name: row.region_name,
                chapter_name: row.chapter_name,
              });
            });

            worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
              if (rowNumber > 2) {
                row.eachCell({ includeEmpty: false }, (cell) => {
                  cell.border = {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    bottom: { style: "thin" },
                    right: { style: "thin" },
                  };
                });
              }
            });

            const buf = await workbook.xlsx.writeBuffer();
            saveAs(new Blob([buf]), `${fileName}.xlsx`);
          }
        })
        .finally(() => {
          setBtnExcelDisabled(false);
        });
    } catch (error) {
      console.error("Training report export error:", error);
      toast.error(error.message || "Failed to export Excel");
    }
  };

  const exportAsPDF = async () => {
    try {
      const doc = new jsPDF("landscape");

      const headers = ["Sl.No.", "Member", "Member ID", "Company", "Total", "Attended", "Absent", "Not Marked", "Att %", "Points", "Last Attended"];
      if (hasRole([1])) headers.push("Country");
      if (hasRole([1, 6])) headers.push("State");
      if (hasRole([1, 6, 7])) headers.push("City");
      if (hasRole([1, 6, 7, 8])) headers.push("Region");
      if (hasRole([1, 6, 7, 8, 2])) headers.push("Circle");

      setBtnPdfDisabled(true);
      await axios
        .get(TRAINING_REPORTS_LIST, {
          params: {
            type: NOPAGINATE,
            isAdmin: 1,
            ...filter,
          },
        })
        .then(async (response) => {
          if (response.status === 200 && response.data.success) {
            const list = response.data.data.users || [];
            if (list.length < 1) {
              toast.error("No data found!");
              return;
            }

            const pdfData = list.map((user, index) => {
              const pData = [
                index + 1,
                user.name,
                user.member_id || "-",
                user.company_name || "-",
                user.total_assigned,
                user.attended_count,
                user.absent_count,
                user.unmarked_count,
                `${user.attendance_percentage}%`,
                user.training_points,
                user.last_attended_date || "-",
              ];
              if (hasRole([1])) pData.push(user.country_name);
              if (hasRole([1, 6])) pData.push(user.state_name);
              if (hasRole([1, 6, 7])) pData.push(user.city_name);
              if (hasRole([1, 6, 7, 8])) pData.push(user.region_name);
              if (hasRole([1, 6, 7, 8, 2])) pData.push(user.chapter_name);
              return pData;
            });

            const pdfTitle = title;
            const textWidth =
              (doc.getStringUnitWidth(pdfTitle) * doc.internal.getFontSize()) /
              doc.internal.scaleFactor;
            const textOffset = (doc.internal.pageSize.width - textWidth) / 2;
            doc.text(pdfTitle, textOffset, 10);

            doc.autoTable({
              head: [headers],
              body: pdfData,
              margin: { top: 20, right: 15, bottom: 20, left: 15 },
              startY: 20,
              headerStyles: {
                fillColor: [207, 32, 48],
                textColor: [255, 255, 255],
                fontSize: 8,
              },
              styles: { lineWidth: 0.1, fontSize: 8 },
            });
            doc.save(`${fileName}.pdf`);
          }
        })
        .finally(() => {
          setBtnPdfDisabled(false);
        });
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error(error.message || "Failed to export PDF");
    }
  };

  const handleDateRangeClear = () => {
    setShowClearBtn(false);
    if (refFlatpickr.current?.flatpickr) {
      refFlatpickr.current.flatpickr.clear();
    }
    setFilter({
      ...filter,
      from_date: "",
      to_date: "",
    });
  };

  useEffect(() => {
    if (!forumRangeReady) return;
    if (!forumFromDate || !forumToDate) {
      setFilter((prev) => ({
        ...(Array.isArray(prev) ? {} : prev),
        from_date: "",
        to_date: "",
      }));
      if (refFlatpickr.current?.flatpickr) {
        refFlatpickr.current.flatpickr.clear();
        setShowClearBtn(false);
      }
      return;
    }
    setFilter((prev) => ({
      ...(Array.isArray(prev) ? {} : prev),
      from_date: forumFromDate,
      to_date: forumToDate,
    }));
    setShowClearBtn(true);
    setTimeout(() => {
      if (refFlatpickr.current?.flatpickr) {
        refFlatpickr.current.flatpickr.setDate([forumFromDate, forumToDate]);
      }
    }, 0);
  }, [forumRangeReady, forumFromDate, forumToDate]);

  useEffect(() => {
    setLoading(true);
    axios
      .get(TRAINING_REPORTS_LIST, {
        params: {
          type: PAGINATE,
          page: page,
          perPage: perPage,
          isAdmin: 1,
          ...filter,
        },
      })
      .then((response) => {
        if (response.status === 200 && response.data.success) {
          setData(response.data.data.users || []);
          setPaginator({
            prev: response.data.data.prev_page_url != null,
            next: response.data.data.next_page_url != null,
            total: response.data.data.total,
            current: response.data.data.current_page,
            last: response.data.data.last_page,
          });
        }
      })
      .catch((err) => {
        console.error("Error loading training report:", err);
      })
      .finally(() => {
        setLoading(false);
      });

    const fetchDropdowns = async () => {
      try {
        const userRes = await axios.get(USER_LIST_DROPDOWN, {
          params: {
            ...filter,
            exclude_other_region_chapter_users: 1,
          },
        });
        setUserOptions(userRes.data?.data || []);

        const countryRes = await axios.get(COUNTRY_LIST_DROPDOWN);
        setCountryOptions(countryRes.data?.data || []);

        const stateRes = await axios.get(STATE_LIST_DROPDOWN, { params: { ...filter } });
        setStateOptions(stateRes.data?.data || []);

        const cityRes = await axios.get(CITY_LIST_DROPDOWN, { params: { ...filter } });
        setCityOptions(cityRes.data?.data || []);

        const regionRes = await axios.get(REGION_LIST_DROPDOWN, { params: { ...filter } });
        setRegionOptions(regionRes.data?.data || []);

        const chapterRes = await axios.get(CHAPTER_LIST_DROPDOWN, { params: { ...filter } });
        setChapterOptions(chapterRes.data?.data || []);
      } catch (error) {
        console.error("Error fetching dropdown options:", error);
      }
    };        fetchDropdowns();
  }, [page, perPage, filter]);

  // Sorting
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedData = React.useMemo(() => {
    let sortableData = [...data];
    if (sortConfig.key) {
      sortableData.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        if (typeof aVal === "string" && typeof bVal === "string") {
          return sortConfig.direction === "asc"
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sortableData;
  }, [data, sortConfig]);

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <i className="bx bx-sort-alt-2 text-muted"></i>;
    return sortConfig.direction === "asc" ? (
      <i className="bx bx-sort-up text-primary"></i>
    ) : (
      <i className="bx bx-sort-down text-primary"></i>
    );
  };

  return (
    <div className="content-wrapper">
      <div className="container-fluid p-0 mt-0 mb-3">
        <div className="col-lg-12">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h5 className="page-title">Training Reports</h5>
              <ol className="breadcrumb">
                <li>
                  <div className="breadcrumb_f">
                    <div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="50"
                        height="50"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#212b39"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                      </svg>
                    </div>
                    <div>
                      <Link to={"/dashboard"}>
                        <span>Dashboard</span>
                      </Link>
                    </div>
                  </div>
                </li>
                <li>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="50"
                    height="50"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#212b39"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </li>
                <li>
                  <span>Reports</span>
                </li>
                <li>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="50"
                    height="50"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#212b39"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </li>
                <li>
                  <span className="active_breadcrumb">Training Reports List</span>
                </li>
              </ol>
            </div>
            <div className="col-lg-6">
              <div className="pt-btn">
                <Link to="/trainings" className="btn rounded-pill btn-outline-primary me-2 ibutton">
                  <i className="bx bx-calendar me-1"></i> Trainings List
                </Link>
                {canExport && (
                  <>
                    <button
                      disabled={btnExcelDisabled}
                      type="button"
                      onClick={exportAsExcel}
                      className="btn rounded-pill btn-primary excel-btn ibutton me-2"
                    >
                      <span className="tf-icons bx bxs-spreadsheet"></span>
                      &nbsp; Excel
                    </button>
                    <button
                      disabled={btnPdfDisabled}
                      type="button"
                      onClick={exportAsPDF}
                      className="btn rounded-pill btn-primary pdf-btn ibutton"
                    >
                      <span className="tf-icons bx bxs-file-pdf"></span>&nbsp; Download PDF
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-grow-1">
        <div className="card icard sticky-table-card">
          <div className="container-fluid f_containter">
            <div className="row mb-4 gx-3">
              {hasRole([1]) && (
                <div className="mb-3 col-md-3">
                  <label className="form-label" htmlFor="country_id">
                    Country
                  </label>
                  <Select
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    name="country_id"
                    isClearable
                    options={countryOptions}
                    onChange={(selected) => {
                      handleStateClear();
                      setFilter({
                        ...filter,
                        country_id: selected?.value,
                        state_id: null,
                        city_id: null,
                        region_id: null,
                        chapter_id: null,
                      });
                    }}
                  />
                </div>
              )}

              {hasRole([1, 6]) && (
                <div className="mb-3 col-md-3">
                  <label className="form-label" htmlFor="state_id">
                    State
                  </label>
                  <Select
                    ref={selectStateRef}
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    name="state_id"
                    isClearable
                    options={stateOptions}
                    onChange={(selected) => {
                      handleCityClear();
                      setFilter({
                        ...filter,
                        state_id: selected?.value,
                        city_id: null,
                        region_id: null,
                        chapter_id: null,
                      });
                    }}
                  />
                </div>
              )}

              {hasRole([1, 6, 7]) && (
                <div className="mb-3 col-md-3">
                  <label className="form-label" htmlFor="city_id">
                    City
                  </label>
                  <Select
                    ref={selectCityRef}
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    name="city_id"
                    isClearable
                    options={cityOptions}
                    onChange={(selected) => {
                      handleRegionClear();
                      setFilter({
                        ...filter,
                        city_id: selected?.value,
                        region_id: null,
                        chapter_id: null,
                      });
                    }}
                  />
                </div>
              )}

              {hasRole([1, 6, 7, 8]) && (
                <div className="mb-3 col-md-3">
                  <label className="form-label" htmlFor="region_id">
                    Region
                  </label>
                  <Select
                    ref={selectRegionRef}
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    name="region_id"
                    isClearable
                    options={regionOptions}
                    onChange={(selected) => {
                      handleChapterClear();
                      setFilter({
                        ...filter,
                        region_id: selected?.value,
                        chapter_id: null,
                      });
                    }}
                  />
                </div>
              )}

              {hasRole([1, 6, 7, 8, 2]) && (
                <div className="mb-3 col-md-3">
                  <label className="form-label" htmlFor="chapter_id">
                    Circle
                  </label>
                  <Select
                    ref={selectChapterRef}
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    name="chapter_id"
                    isClearable
                    options={chapterOptions}
                    onChange={(selected) => {
                      if (refFlatpickr.current && refFlatpickr.current.flatpickr) {
                        refFlatpickr.current.flatpickr.clear();
                        setShowClearBtn(false);
                      }
                      setFilter({
                        ...filter,
                        chapter_id: selected?.value,
                        from_date: "",
                        to_date: "",
                      });
                    }}
                  />
                </div>
              )}

              <div className="mb-3 col-md-3">
                <label className="form-label" htmlFor="user_id">
                  Member
                </label>
                <Select
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  name="user_id"
                  isClearable
                  options={userOptions}
                  onChange={(selected) =>
                    setFilter({ ...filter, user_id: selected?.value })
                  }
                />
              </div>

              <div className="col-md-3">
                <div className="mb-3 dateRangeDiv">
                  <label className="form-label" htmlFor="date_range">
                    Date Range
                  </label>
                  <div className="datepicker_wrapper">
                    <div className="datepicker">
                      <Flatpickr
                        ref={refFlatpickr}
                        className="form-control"
                        options={{
                          mode: "range",
                          dateFormat: "d/m/Y",
                          onChange: (selected) => {
                            setShowClearBtn(true);
                            if (selected[0] && selected[1]) {
                              setFilter({
                                ...filter,
                                from_date: moment(selected[0]).format("DD/MM/YYYY"),
                                to_date: moment(selected[1]).format("DD/MM/YYYY"),
                              });
                            }
                          },
                          onClose: (selected) => {
                            if (!selected[1]) {
                              setShowClearBtn(false);
                            }
                          },
                        }}
                        placeholder="Date Range"
                      />
                      {showClearBtn && (
                        <button
                          className="border-0 clearDateRangeBtn"
                          title="Clear"
                          type="button"
                          onClick={handleDateRangeClear}
                        >
                          <i className="bx bx-x"></i>
                        </button>
                      )}
                    </div>
                    <div
                      className="cal_icon"
                      onClick={() => refFlatpickr.current?.flatpickr?.open()}
                    >
                      <span className="input-group-text calendar-icon"></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {data.length > 0 && (
            <Pagination
              paginator={paginator}
              page={(no) => setPage(no)}
              perPage={perPage}
              setPerPage={setPerPage}
            />
          )}

          <StickyTable refreshDeps={[data, loading]}>
            <table className="table table-striped itable">
              <thead>
                <tr>
                  <th>Sl. No.</th>
                  <th>Member</th>
                  <th>Member ID</th>
                  <th>Company</th>
                  <th>Mobile</th>
                  <th>Total Trainings</th>
                  <th>Attended</th>
                  <th>Absent</th>
                  <th>Not Marked</th>
                  <th>Attendance %</th>
                  <th>Points</th>
                  <th>Last Attended</th>
                  {hasRole([1]) && <th>Country</th>}
                  {hasRole([1, 6]) && <th>State</th>}
                  {hasRole([1, 6, 7]) && <th>City</th>}
                  {hasRole([1, 6, 7, 8]) && <th>Region</th>}
                  {hasRole([1, 6, 7, 8, 2]) && <th>Circle</th>}
                </tr>
              </thead>
              <tbody className="table-border-bottom-0">
                {loading ? (
                  <Loader loading={loading} colspan="17" />
                ) : data.length > 0 ? (
                  data.map((user) => (
                    <tr key={user.id}>
                      <td>{user.sno}</td>
                      <td>
                        <span className="table_img pr-2">
                          <img src={user.profile_image} alt={user.name} />
                        </span>
                        <span className="fw-semibold">{user.name}</span>
                      </td>
                      <td>
                        <span className="badge bg-label-secondary">{user.member_id || "-"}</span>
                      </td>
                      <td>{user.company_name || "-"}</td>
                      <td>
                        {user.mobile_number ? `+${user.mobile_country_code || 91} ${user.mobile_number}` : "-"}
                      </td>
                      <td>
                        <span className="fw-bold">{user.total_assigned}</span>
                      </td>
                      <td>
                        <span className="badge bg-label-success">{user.attended_count}</span>
                      </td>
                      <td>
                        <span className="badge bg-label-danger">{user.absent_count}</span>
                      </td>
                      <td>
                        <span className="badge bg-label-warning">{user.unmarked_count}</span>
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            user.attendance_percentage >= 75
                              ? "bg-label-success"
                              : user.attendance_percentage >= 50
                              ? "bg-label-warning"
                              : "bg-label-danger"
                          }`}
                        >
                          {user.attendance_percentage}%
                        </span>
                      </td>
                      <td>
                        <span className="badge bg-label-primary">{user.training_points}</span>
                      </td>
                      <td>{user.last_attended_date || "-"}</td>
                      {hasRole([1]) && <td>{user.country_name}</td>}
                      {hasRole([1, 6]) && <td>{user.state_name}</td>}
                      {hasRole([1, 6, 7]) && <td>{user.city_name}</td>}
                      {hasRole([1, 6, 7, 8]) && <td>{user.region_name}</td>}
                      {hasRole([1, 6, 7, 8, 2]) && <td>{user.chapter_name}</td>}
                    </tr>
                  ))
                ) : (
                  <NoDataFound colspan="17" message="No Data Found" />
                )}
              </tbody>
            </table>
          </StickyTable>

          {data.length > 0 && (
            <Pagination
              paginator={paginator}
              page={(no) => setPage(no)}
              perPage={perPage}
              setPerPage={setPerPage}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TrainingReportsList;
